import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as PricingIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useCreatePricingTierMutation,
  useUpdatePricingTierMutation,
  useGetPricingTierQuery,
} from '../store/services/customerApi'
import { PricingTierCreateData, PricingTierUpdateData } from '../types/customer'
import PageHeader from '../components/ui/PageHeader'

interface PricingRule {
  type: 'category' | 'product' | 'volume'
  target_id?: string
  min_quantity?: number
  max_quantity?: number
  discount_percent?: number
  fixed_price?: number
}

const PricingTierFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const isEditing = Boolean(id)

  // Form state
  const [formData, setFormData] = useState<PricingTierCreateData>({
    name: '',
    rules: {},
    margin_percent: 0,
    margin_amount: 0,
    is_active: true,
  })

  const [pricingRules, setPricingRules] = useState<PricingRule[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // API hooks
  const { data: tier, isLoading: tierLoading } = useGetPricingTierQuery(id!, {
    skip: !isEditing,
  })
  
  const [createPricingTier] = useCreatePricingTierMutation()
  const [updatePricingTier] = useUpdatePricingTierMutation()

  // Initialize form data when tier changes
  useEffect(() => {
    if (tier) {
      setFormData({
        name: tier.name,
        rules: tier.rules || {},
        margin_percent: tier.margin_percent,
        margin_amount: tier.margin_amount,
        is_active: tier.is_active,
      })

      // Parse pricing rules
      const parsedRules: PricingRule[] = []
      if (tier.rules && typeof tier.rules === 'object') {
        Object.entries(tier.rules).forEach(([, value]) => {
          if (typeof value === 'object' && value !== null) {
            const rule = value as any
            parsedRules.push({
              type: rule.type || 'category',
              target_id: rule.target_id,
              min_quantity: rule.min_quantity,
              max_quantity: rule.max_quantity,
              discount_percent: rule.discount_percent,
              fixed_price: rule.fixed_price,
            })
          }
        })
      }
      setPricingRules(parsedRules)
    }
    setErrors({})
  }, [tier])

  const handleInputChange = (field: keyof PricingTierCreateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const addPricingRule = () => {
    setPricingRules(prev => [...prev, { 
      type: 'category',
      discount_percent: 0 
    }])
  }

  const updatePricingRule = (index: number, field: keyof PricingRule, value: any) => {
    setPricingRules(prev => prev.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    ))
  }

  const removePricingRule = (index: number) => {
    setPricingRules(prev => prev.filter((_, i) => i !== index))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Tier name is required'
    }

    if (formData.margin_percent !== undefined && (formData.margin_percent < 0 || formData.margin_percent > 100)) {
      newErrors.margin_percent = 'Margin percent must be between 0 and 100'
    }

    if (formData.margin_amount !== undefined && formData.margin_amount < 0) {
      newErrors.margin_amount = 'Margin amount cannot be negative'
    }

    // Validate pricing rules
    pricingRules.forEach((rule, index) => {
      if (rule.type === 'volume') {
        if (!rule.min_quantity || rule.min_quantity <= 0) {
          newErrors[`rule_${index}_min_quantity`] = 'Min quantity is required for volume rules'
        }
      }
      
      if (rule.discount_percent && (rule.discount_percent < 0 || rule.discount_percent > 100)) {
        newErrors[`rule_${index}_discount`] = 'Discount percent must be between 0 and 100'
      }

      if (rule.fixed_price && rule.fixed_price < 0) {
        newErrors[`rule_${index}_price`] = 'Fixed price cannot be negative'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    // Convert pricing rules to object format
    const rulesObj: Record<string, any> = {}
    pricingRules.forEach((rule, index) => {
      rulesObj[`rule_${index}`] = {
        type: rule.type,
        target_id: rule.target_id,
        min_quantity: rule.min_quantity,
        max_quantity: rule.max_quantity,
        discount_percent: rule.discount_percent,
        fixed_price: rule.fixed_price,
      }
    })

    const submitData = {
      ...formData,
      rules: rulesObj,
    }

    setLoading(true)
    try {
      if (isEditing && tier) {
        const updateData: PricingTierUpdateData = submitData
        await updatePricingTier({ id: tier.id, data: updateData }).unwrap()
        enqueueSnackbar('Pricing tier updated successfully', { variant: 'success' })
      } else {
        await createPricingTier(submitData).unwrap()
        enqueueSnackbar('Pricing tier created successfully', { variant: 'success' })
      }
      navigate('/customers')
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.detail || 'Failed to save pricing tier',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/customers')
  }

  if (tierLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading pricing tier...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/customers')}
          sx={{ textDecoration: 'none' }}
        >
          Customers
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Pricing Tier' : 'Add New Pricing Tier'}
        </Typography>
      </Breadcrumbs>

      <PageHeader
        title={isEditing ? 'Edit Pricing Tier' : 'Add New Pricing Tier'}
        subtitle={isEditing ? `Update pricing tier configuration for ${tier?.name}` : 'Create a new pricing tier with custom rules'}
        actions={[
          {
            label: 'Cancel',
            onClick: handleCancel,
            variant: 'outlined',
            icon: <ArrowBackIcon />,
            disabled: loading,
          },
          {
            label: loading ? 'Saving...' : isEditing ? 'Update Tier' : 'Create Tier',
            onClick: handleSubmit,
            variant: 'contained',
            color: 'secondary',
            icon: <SaveIcon />,
            disabled: loading,
          },
        ]}
      />

      <Paper sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PricingIcon color="primary" />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tier Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>

          {/* Margin Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mt: 2, mb: 2 }}>
              Default Margin Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Margin Percentage (%)"
              type="number"
              value={formData.margin_percent}
              onChange={(e) => handleInputChange('margin_percent', Number(e.target.value))}
              error={!!errors.margin_percent}
              helperText={errors.margin_percent || 'Default margin percentage for this tier'}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Margin Amount (₹)"
              type="number"
              value={formData.margin_amount}
              onChange={(e) => handleInputChange('margin_amount', Number(e.target.value))}
              error={!!errors.margin_amount}
              helperText={errors.margin_amount || 'Fixed margin amount (alternative to percentage)'}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          {/* Pricing Rules */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Pricing Rules
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addPricingRule}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Add Rule
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {pricingRules.map((rule, index) => (
              <Card key={index} sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Rule {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removePricingRule(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rule Type</InputLabel>
                        <Select
                          value={rule.type}
                          onChange={(e) => updatePricingRule(index, 'type', e.target.value)}
                          label="Rule Type"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="category">Category</MenuItem>
                          <MenuItem value="product">Product</MenuItem>
                          <MenuItem value="volume">Volume</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(rule.type === 'category' || rule.type === 'product') && (
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label={`${rule.type === 'category' ? 'Category' : 'Product'} ID`}
                          value={rule.target_id || ''}
                          onChange={(e) => updatePricingRule(index, 'target_id', e.target.value)}
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    )}

                    {rule.type === 'volume' && (
                      <>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Min Qty"
                            type="number"
                            value={rule.min_quantity || ''}
                            onChange={(e) => updatePricingRule(index, 'min_quantity', Number(e.target.value))}
                            size="small"
                            error={!!errors[`rule_${index}_min_quantity`]}
                            helperText={errors[`rule_${index}_min_quantity`]}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Max Qty"
                            type="number"
                            value={rule.max_quantity || ''}
                            onChange={(e) => updatePricingRule(index, 'max_quantity', Number(e.target.value))}
                            size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Discount %"
                        type="number"
                        value={rule.discount_percent || ''}
                        onChange={(e) => updatePricingRule(index, 'discount_percent', Number(e.target.value))}
                        size="small"
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        error={!!errors[`rule_${index}_discount`]}
                        helperText={errors[`rule_${index}_discount`]}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Fixed Price (₹)"
                        type="number"
                        value={rule.fixed_price || ''}
                        onChange={(e) => updatePricingRule(index, 'fixed_price', Number(e.target.value))}
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        error={!!errors[`rule_${index}_price`]}
                        helperText={errors[`rule_${index}_price`]}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {rule.type === 'category' && 'Apply to all products in specified category'}
                      {rule.type === 'product' && 'Apply to specific product'}
                      {rule.type === 'volume' && 'Apply based on order quantity'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {pricingRules.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No pricing rules defined. The default margin settings will be used for all products.
              </Alert>
            )}
          </Grid>

          {/* Help Information */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Pricing Rule Priority:</strong>
                <br />
                1. Fixed Price (if specified) overrides all other calculations
                <br />
                2. Discount Percentage is applied to base product price
                <br />
                3. Default margin settings are used if no specific rules match
                <br />
                4. Volume rules take precedence over category/product rules when quantity thresholds are met
              </Typography>
            </Alert>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleCancel}
                disabled={loading}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
                sx={{ minWidth: 160, borderRadius: 2 }}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Tier' : 'Create Tier'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default PricingTierFormPage